import { X } from 'lucide-react';
import { fileUrl, type AttachmentKind } from '../api/client.ts';
import type { MessageView } from '../chat/ChatContext.tsx';
import { albumRows, ALBUM_GAP, ALBUM_WIDTH } from '../lib/album.ts';
import { formatDuration, formatSendingLabel } from '../lib/format.ts';
import { PlayIcon } from './MediaIcons.tsx';
import { TextBody } from './TextBody.tsx';
import './AlbumBubble.css';

/**
 * A group of photos and videos, in the thread and on the compose screen.
 *
 * Both draw the same grid from the same table (see lib/album.ts), and that is
 * the point rather than a convenience: the compose screen's job is to show the
 * grouping the message will actually have, so a preview that merely resembled it
 * would be worse than none. What differs between the two is what a tile does —
 * one opens the viewer, the other drops the item — and that is all.
 *
 * The gaps between tiles are not a colour. The grid's own background is the
 * thread's, and 2px of it shows through, which is what makes the hairlines
 * follow whatever wallpaper is behind them without anything having to be told.
 */

/** One tile's worth of what there is to draw. */
export interface AlbumTileSource {
  /** The picture, or `null` when there is none — an undecodable video's frame. */
  src: string | null;
  kind: AttachmentKind;
  /** Videos only, shown as a pill in the corner. */
  durationMs: number | null;
  /**
   * The compose screen's only: a picked video whose frame is still being read,
   * which turns the play badge into a spinner. There is no such state in the
   * thread — a message is sent with its posters already in hand.
   */
  pending?: boolean;
}

export function AlbumGrid({
  items,
  onOpen,
  onRemove,
}: {
  items: AlbumTileSource[];
  /** Tapping a tile opens it in the viewer; absent on the compose screen. */
  onOpen?: (index: number) => void;
  /** The ✕ that drops an item; absent in the thread, where nothing is editable. */
  onRemove?: (index: number) => void;
}) {
  return (
    <div className="album-grid" style={{ width: ALBUM_WIDTH, gap: ALBUM_GAP }}>
      {albumRows(items.length).map((row, rowIndex) => (
        <div className="album-row" key={rowIndex} style={{ gap: ALBUM_GAP }}>
          {row.tiles.map((tile) => {
            const item = items[tile.index];
            if (!item) return null;
            return (
              <div
                className="album-tile"
                key={tile.index}
                style={{ width: tile.width, height: tile.height }}
              >
                {item.src ? (
                  <img
                    className="album-tile-picture"
                    src={item.src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="album-tile-blank" />
                )}

                {/* The badge sits in the same place either way, so a frame
                    arriving swaps the spinner for the triangle rather than
                    moving anything. */}
                {item.kind === 'video' && (
                  <span className="album-tile-play" aria-hidden="true">
                    {item.pending ? (
                      <span className="album-tile-spinner" />
                    ) : (
                      <PlayIcon size={13} />
                    )}
                  </span>
                )}

                {item.kind === 'video' && item.durationMs !== null && (
                  <span className="album-tile-duration">{formatDuration(item.durationMs)}</span>
                )}

                {/* Everything past the ninth, said in one word rather than by
                    growing the grid — see albumRows. */}
                {tile.overflow > 0 && (
                  <span className="album-tile-overflow">+{tile.overflow}</span>
                )}

                {/* A button laid over the whole tile rather than the tile being
                    one: the play badge and the duration pill sit on top of the
                    picture, and nesting them inside a button is not something a
                    screen reader can make sense of. */}
                {onOpen && (
                  <button
                    type="button"
                    className="album-tile-open"
                    aria-label={item.kind === 'video' ? 'Открыть видео' : 'Открыть фото'}
                    onClick={() => onOpen(tile.index)}
                  />
                )}

                {onRemove && (
                  <button
                    type="button"
                    className="album-tile-remove"
                    aria-label="Убрать"
                    onClick={() => onRemove(tile.index)}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * The tiles of a message, wherever its pictures happen to be.
 *
 * While an album is going up they are object URLs for the files on this device,
 * which is what makes the bubble appear whole rather than as nine grey squares
 * counting to 100% — the sender is looking at the photos they just picked. Once
 * the server has them, the same tiles are its thumbnails.
 */
function tilesOf(message: MessageView): AlbumTileSource[] {
  // Also after a failure, and that is not a detail: a group that did not go up
  // has no attachments on the server to draw from, so without this it collapses
  // to its caption alone — a bare «не отправлено» over nothing, with no way to
  // see which pictures it was. They are still on this device; it can show them.
  if (message.localItems && (message.status === 'sending' || message.status === 'failed')) {
    return message.localItems.map((item) => ({
      src: item.previewUrl,
      kind: item.kind,
      durationMs: item.durationMs,
    }));
  }
  return message.attachments.map((item) => ({
    src: item.hasPreview
      ? fileUrl(message.id, { variant: 'thumb', index: item.index })
      : null,
    kind: item.kind,
    durationMs: item.durationMs,
  }));
}

export function AlbumBubble({
  message,
  onOpen,
}: {
  message: MessageView;
  onOpen: (index: number) => void;
}) {
  const items = tilesOf(message);
  const caption = message.body?.trim() ?? '';
  const uploading = message.status === 'sending';

  return (
    <div className={`album album-${message.fromMe ? 'out' : 'in'}`}>
      <AlbumGrid items={items} onOpen={onOpen} />

      {/* Inside the clipped wrapper, so its bottom corners are the bubble's own
          with no second radius to keep in step. */}
      {caption !== '' && (
        <div className="album-caption">
          <TextBody body={caption} />
        </div>
      )}

      {uploading && (
        <span className="album-progress">
          <span className="album-spinner" />
          {formatSendingLabel(message.sendPhase ?? 'uploading', message.progressPct ?? 0)}
        </span>
      )}
    </div>
  );
}
